import { PurchaseOrderDeliveryStatusEnum, PurchaseOrderInvoicedStatusEnum, PurchaseOrderStatusEnum, VendorTypeEnum } from "@finway-group/shared/lib/models"
import { Form } from "antd"
import { t } from "i18next"
import React, { useEffect } from "react"
import { FilterFormProps } from "~Components/filter/filterTypes"
import { FormInputEnum } from "~Components/form/form.types"
import { FormMinMax } from "~Components/form/formMinMax"
import { FormMinMaxDate } from "~Components/form/formMinMaxDate"
import { FormSelect } from "~Components/form/formSelect"
import { VendorSelect } from "~Components/forms/expenseForm/commonInputFields/VendorSelect"
import { useEmployees } from "~Shared/queries/employee.queries"
import { CollapseWrapper } from "~Shared/wrapper/collapse.wrapper"

export interface PurchaseOrderFilterFormValues {
    requestedBy?: string
    vendor?: string
    minDateOfIssue?: string
    maxDateOfIssue?: string
    deliveryStatus?: string
    invoicedStatus?: string
    minAmount?: number
    maxAmount?: number
    purchaseOrderState?: string
}

export const PurchaseOrderFilterForm = ({ formInstance, filters, onFilter }: FilterFormProps<PurchaseOrderFilterFormValues>) => {
    const { data: employees = [] } = useEmployees({ excludeDeleted: true })

    useEffect(() => {
        formInstance.resetFields()
    }, [filters])

    return (
        <Form id="purchaseOrderFilterForm" className="form--filter" form={formInstance} onFinish={onFilter}>
            {/* Requester  */}
            <FormSelect
                name="requestedBy"
                header={t("purchaseOrders:columns.requester")}
                initialValue={filters.requestedBy}
                options={employees}
                displayImages
                isCollapse
                showSearch
                fallback
            />

            {/* Vendor  */}
            <CollapseWrapper keyCode="" header={t("purchaseOrders:columns.vendor")} isCollapse isActive={!!filters.vendor}>
                <VendorSelect
                    shouldShowOnlyVerifiedVendors={false}
                    shouldRenderSelectDropdown={false}
                    selectedVendorType={VendorTypeEnum.VENDOR}
                    selectedCreditorId={filters.vendor}
                    formProps={{ name: "vendor", form: formInstance }}
                />
            </CollapseWrapper>

            {/* Date of issue  */}
            <FormMinMaxDate
                header={t("purchaseOrders:columns.dateOfIssue")}
                min={{
                    name: "minDateOfIssue",
                    label: t("input:expense_filter.min_date"),
                    placeholder: t("placeholder:expense_filter.min_date"),
                    initialValue: filters.minDateOfIssue,
                }}
                max={{
                    name: "maxDateOfIssue",
                    label: t("input:expense_filter.max_date"),
                    placeholder: t("placeholder:expense_filter.max_date"),
                    initialValue: filters.maxDateOfIssue,
                }}
                formInstance={formInstance}
            />

            {/* Delivery status  */}
            <FormSelect
                name="deliveryStatus"
                header={t("purchaseOrders:columns.deliveryStatus")}
                initialValue={filters.deliveryStatus}
                options={Object.values(PurchaseOrderDeliveryStatusEnum).map((item) => ({ param: t(`purchaseOrders:deliveryStatus.full.${item}`), value: item }))}
                isCollapse
                fallback
            />

            {/* Involice status  */}
            <FormSelect
                name="invoicedStatus"
                header={t("purchaseOrders:columns.invoicedStatus")}
                initialValue={filters.invoicedStatus}
                options={Object.values(PurchaseOrderInvoicedStatusEnum).map((item) => ({ param: t(`purchaseOrders:invoicedStatus.full.${item}`), value: item }))}
                isCollapse
                fallback
            />
            {/* Amount  */}
            <FormMinMax
                header={t("purchaseOrders:columns.amount")}
                inputType={FormInputEnum.NUMBER}
                min={{
                    name: "minAmount",
                    label: t("input:filter_form.min_amount"),
                    placeholder: t("placeholder:expense_filter.min_amount"),
                    onChange: () => formInstance.validateFields(["minAmount", "maxAmount"]),
                    initialValue: filters.minAmount,
                }}
                max={{
                    name: "maxAmount",
                    label: t("input:filter_form.max_amount"),
                    placeholder: t("placeholder:expense_filter.max_amount"),
                    onChange: () => formInstance.validateFields(["minAmount", "maxAmount"]),
                    initialValue: filters.maxAmount,
                }}
            />

            {/* Status  */}
            <FormSelect
                name="purchaseOrderState"
                header={t("purchaseOrders:columns.status")}
                initialValue={filters.purchaseOrderState}
                options={Object.values(PurchaseOrderStatusEnum)
                    .filter((value: number) => typeof value === "number")
                    .filter((item: number) => ![PurchaseOrderStatusEnum.CONFIRMATION, PurchaseOrderStatusEnum.CALCULATE_LINE_ITEMS].includes(item))
                    .map((item) => ({ param: t(`enum:PurchaseOrderStateEnum.${item}`), value: item }))}
                isCollapse
                fallback
            />
        </Form>
    )
}
